/**
 * Agent domain types — mirror of ``packages/roboto/src/roboto/ai/agent``.
 *
 * Keep these in sync with the SDK record by hand for now. An agent is a
 * pre-filled ``StartAgentSessionRequest`` with declared variables; invoking
 * the agent substitutes ``{{name}}`` placeholders in the body and starts
 * an AgentSession.
 */

/**
 * UI hint for how the launch page should render a variable's input control.
 * The substitution engine does not branch on this value — every supplied
 * value is coerced to ``str`` on the server before being spliced into the
 * placeholder. Types are metadata for picker selection.
 */
export enum TemplateVariableType {
  STRING = "string",
  DATASET_ID = "dataset_id",
  DEVICE_ID = "device_id",
}

/**
 * Who is allowed to read an AgentSession after it has been created.
 *
 * Mirrors ``SessionVisibility`` on the backend. ``PRIVATE`` restricts reads
 * to the ``created_by`` user and Roboto admins; ``ORG`` opens the session
 * to every member of the org. The launch page defaults to ``ORG`` since
 * agents exist to share workflows.
 */
export enum SessionVisibility {
  PRIVATE = "private",
  ORG = "org",
}

/**
 * A declared variable on an Agent.
 *
 * Backend validates that the set of declared names equals the set of
 * placeholders found in ``request_template`` exactly, so the editor UI can
 * surface a mismatch warning client-side before the save 422s.
 */
export interface TemplateVariable {
  /** Lookup key for substitution; must match ``^[a-zA-Z_][a-zA-Z0-9_.]*$``. */
  name: string;
  /** UI hint for the input control on the launch page. */
  type: TemplateVariableType;
  /** Human-readable explanation rendered next to the input. */
  description?: string | null;
  /**
   * If ``true``, the resolver errors when no value is supplied at launch
   * time and ``default`` is also unset.
   */
  required: boolean;
  /**
   * Default value substituted when the caller omits the variable. Coerced
   * to a string server-side; types are a UI concern.
   */
  default?: string | null;
}

/**
 * Persistent record of an Agent as returned by the server.
 *
 * ``request_template`` is the full StartAgentSessionRequest with optional
 * ``{{var}}`` placeholders in any string leaf. The shape is intentionally
 * left as ``Record<string, unknown>`` rather than a typed mirror of the
 * SDK record so the type does not have to track every backward-compatible
 * field added to the request body; ``buildRequestTemplate`` /
 * ``parseSavedAgent`` handle the shape the editor cares about.
 */
export interface AgentRecord {
  agent_id: string;
  org_id: string;
  name: string;
  description?: string | null;
  request_template: Record<string, unknown>;
  variables: TemplateVariable[];
  created: string;
  created_by: string;
  modified: string;
  modified_by: string;
}

/**
 * Wire payload for ``POST /v1/ai/agents``.
 *
 * Server fills ``agent_id``, ``org_id``, the timestamp fields, and
 * ``created_by`` / ``modified_by`` from the caller's identity.
 */
export interface CreateAgentRequest {
  name: string;
  description?: string | null;
  request_template: Record<string, unknown>;
  variables: TemplateVariable[];
}

/**
 * Wire payload for ``PUT /v1/ai/agents/<agent_id>``.
 *
 * Every field is optional; omitting a field is the wire-format equivalent
 * of the backend's ``NotSet`` sentinel and leaves the existing value
 * untouched. The backend re-runs the variable-vs-placeholder consistency
 * check on the merged record before persisting.
 */
export interface UpdateAgentRequest {
  name?: string;
  description?: string | null;
  request_template?: Record<string, unknown>;
  variables?: TemplateVariable[];
}

/**
 * Wire payload for ``POST /v1/ai/agents/<agent_id>/invoke``.
 *
 * The launch handler resolves the agent body against ``values`` and
 * starts a new AgentSession with the supplied ``visibility``. ``ORG`` is
 * the default because agents exist to share workflows; the launch page
 * lets the caller opt the run back into ``PRIVATE``.
 */
export interface LaunchAgentRequest {
  values: Record<string, string>;
  visibility: SessionVisibility;
}
